import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { Coins, Gauge, FileWarning, PauseCircle, HeartCrack, Check } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { BILLING_ENABLED, planLabel, formatNumber, TOPUP_OPTIONS, topupPrice, startCheckout } from "@/lib/plans";

// The pop-ups that stand between a person and a paywall or a limit:
//   out of credits · limit reached (daily reveals, rows per upload, paused
//   account) · buy credits · cancel plan.
// One provider so any page can open them with useBillingDialogs().

const BillingDialogsContext = createContext(null);

export const useBillingDialogs = () => useContext(BillingDialogsContext);

function IconChip({ icon: Icon, tone = "violet" }) {
  const cls = tone === "warning" ? "bg-warning-soft text-warning"
    : tone === "danger" ? "bg-danger-soft text-destructive" : "bg-violet-soft text-violet-text";
  return (
    <span className={`grid h-11 w-11 place-items-center rounded-full ${cls}`}>
      <Icon className="h-5 w-5" />
    </span>
  );
}

export function BillingDialogsProvider({ children }) {
  const [open, setOpen] = useState(null); // { kind, ...info }
  const close = useCallback(() => setOpen(null), []);
  const navigate = useNavigate();

  const api = useMemo(() => ({
    showOutOfCredits: (info = {}) => setOpen({ kind: "credits", ...info }),
    showLimitReached: (info = {}) => setOpen({ kind: "limit", ...info }),
    showBuyCredits: () => setOpen({ kind: "buy" }),
    showCancel: () => setOpen({ kind: "cancel" }),
    /**
     * Turn a failed reveal into the right pop-up. Returns true when handled,
     * false when the caller should show its own message.
     */
    handleRevealError: (res) => {
      if (res?.error === "no_credits") { setOpen({ kind: "credits", required: res.required, available: res.available }); return true; }
      if (res?.error === "daily_limit") { setOpen({ kind: "limit", reason: "daily", limit: res.limit, used: res.used }); return true; }
      if (res?.error === "account_frozen") { setOpen({ kind: "limit", reason: "frozen" }); return true; }
      return false;
    },
  }), []);

  const goPlans = () => { close(); navigate("/choose-plan"); };

  return (
    <BillingDialogsContext.Provider value={api}>
      {children}
      <OutOfCreditsDialog state={open?.kind === "credits" ? open : null} onClose={close} onPlans={goPlans}
        onBuy={() => setOpen({ kind: "buy" })} />
      <LimitReachedDialog state={open?.kind === "limit" ? open : null} onClose={close} onPlans={goPlans} />
      <BuyCreditsDialog open={open?.kind === "buy"} onClose={close} onPlans={goPlans} />
      <CancelPlanDialog open={open?.kind === "cancel"} onClose={close} />
    </BillingDialogsContext.Provider>
  );
}

function OutOfCreditsDialog({ state, onClose, onPlans, onBuy }) {
  const notEnough = state && state.available > 0;
  return (
    <Dialog open={!!state} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="out-of-credits-dialog">
        <IconChip icon={Coins} tone="warning" />
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {notEnough ? "Not enough credits" : "You're out of credits"}
          </DialogTitle>
          <DialogDescription className="text-[15px] leading-6">
            {notEnough
              ? `Needs ${state.required} credits — you have ${state.available}.`
              : "No credits remaining. Upgrade your plan."}
            {" "}Plans add credits every month and unused ones roll over. You're never charged when we can't find a contact.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={onBuy}>Buy credits</Button>
          <Button onClick={onPlans} data-testid="out-of-credits-see-plans">See plans</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LimitReachedDialog({ state, onClose, onPlans }) {
  const { user } = useAuth();
  const reason = state?.reason;
  const copy = reason === "daily" ? {
    icon: Gauge, tone: "warning", title: "You've hit today's reveal limit",
    body: `Your ${planLabel(user?.plan)} plan allows ${formatNumber(state.limit)} reveals a day, and you've used ${formatNumber(state.used ?? state.limit)}. The count resets at midnight UTC. Search stays unlimited.`,
    upgrade: true,
  } : reason === "rows" ? {
    icon: FileWarning, tone: "warning", title: "That file is too big",
    body: `Your plan allows up to ${formatNumber(state.max)} rows per upload${state.rows ? `, and this one has ${formatNumber(state.rows)}` : ""}. Split it into smaller files and run them one after another.`,
  } : reason === "frozen" ? {
    icon: PauseCircle, tone: "danger", title: "Your account is paused",
    body: "Reveals, Enrich and exports are switched off on this account. Search still works. Email support@inboundlabs.app and we'll sort it out.",
  } : null;

  return (
    <Dialog open={!!state} onOpenChange={(o) => !o && onClose()}>
      {copy && (
        <DialogContent className="max-w-md" data-testid="limit-reached-dialog">
          <IconChip icon={copy.icon} tone={copy.tone} />
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{copy.title}</DialogTitle>
            <DialogDescription className="text-[15px] leading-6">{copy.body}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={onClose}>Got it</Button>
            {copy.upgrade && <Button onClick={onPlans}>See higher limits</Button>}
          </DialogFooter>
        </DialogContent>
      )}
    </Dialog>
  );
}

function BuyCreditsDialog({ open, onClose, onPlans }) {
  const [credits, setCredits] = useState(TOPUP_OPTIONS[0]);
  const [busy, setBusy] = useState(false);
  const price = topupPrice(credits);

  const buy = async () => {
    if (!BILLING_ENABLED) return;
    setBusy(true);
    try {
      await startCheckout({ topupCredits: credits });
    } catch (e) {
      toast.error(e.message);
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="buy-credits-dialog">
        <IconChip icon={Coins} />
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Buy credits</DialogTitle>
          <DialogDescription className="text-[15px] leading-6">
            $5 for every 1,000 credits, added to your balance as soon as you pay.
          </DialogDescription>
        </DialogHeader>
        <div role="radiogroup" aria-label="How many credits" className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {TOPUP_OPTIONS.map((n) => (
            <button key={n} type="button" role="radio" aria-checked={credits === n} onClick={() => setCredits(n)}
              data-testid={`topup-${n}`}
              className={`rounded-lg border px-3 py-2.5 text-left transition-colors ${
                credits === n ? "border-primary bg-violet-soft" : "border-border hover:bg-surface-hover"}`}>
              <div className="text-sm font-semibold tabular-nums">{formatNumber(n)} credits</div>
              <div className="text-xs text-muted-foreground tabular-nums">${formatNumber(topupPrice(n))}</div>
            </button>
          ))}
        </div>
        {!BILLING_ENABLED && (
          <p className="text-sm text-muted-foreground" data-testid="topup-billing-off">
            Payments open soon. You'll be able to buy credits here the day they go live.
          </p>
        )}
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={onPlans}>See plans instead</Button>
          <Button onClick={buy} disabled={!BILLING_ENABLED || busy} data-testid="topup-buy">
            Buy {formatNumber(credits)} credits · ${formatNumber(price)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const CANCEL_REASONS = [
  { id: "too_expensive", label: "It's too expensive" },
  { id: "not_enough_matches", label: "Not enough contacts for my market" },
  { id: "data_quality", label: "The data wasn't accurate enough" },
  { id: "project_done", label: "I only needed it for one project" },
  { id: "switching", label: "I'm switching to another tool" },
  { id: "other", label: "Something else" },
];

function CancelPlanDialog({ open, onClose }) {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState("reason"); // reason → offer → done
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setStep("reason"); setReason(""); setDetails(""); setBusy(false); };
  const finish = () => { reset(); onClose(); };

  const submit = async (outcome) => {
    setBusy(true);
    const { data, error } = await supabase.rpc("submit_cancellation", {
      p_reason: reason, p_details: details || null, p_outcome: outcome,
    });
    setBusy(false);
    if (error || data?.error) { toast.error("Couldn't save that. Please try again."); return; }
    await refreshUser();
    if (outcome === "kept") { toast.success("Great, your plan stays as it is."); finish(); }
    else setStep("done");
  };

  const endDate = user?.current_period_end ? format(new Date(user.current_period_end), "d MMM yyyy") : null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && finish()}>
      <DialogContent className="max-w-lg" data-testid="cancel-plan-dialog">
        {step === "reason" && (
          <>
            <IconChip icon={HeartCrack} />
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">Why are you cancelling?</DialogTitle>
              <DialogDescription>One click helps us fix what isn't working.</DialogDescription>
            </DialogHeader>
            <div role="radiogroup" aria-label="Reason" className="grid gap-2">
              {CANCEL_REASONS.map((r) => (
                <button key={r.id} type="button" role="radio" aria-checked={reason === r.id}
                  onClick={() => setReason(r.id)} data-testid={`cancel-reason-${r.id}`}
                  className={`flex items-center justify-between rounded-lg border px-4 py-2.5 text-left text-sm transition-colors ${
                    reason === r.id ? "border-primary bg-violet-soft text-foreground" : "border-border hover:bg-surface-hover"}`}>
                  {r.label}
                  {reason === r.id && <Check className="h-4 w-4 text-violet-text" />}
                </button>
              ))}
            </div>
            <Textarea value={details} onChange={(e) => setDetails(e.target.value)} rows={3}
              placeholder="Anything else we should know? (optional)" data-testid="cancel-details" />
            <DialogFooter className="gap-2 sm:gap-2">
              <Button variant="outline" onClick={finish}>Keep my plan</Button>
              <Button disabled={!reason} onClick={() => setStep("offer")} data-testid="cancel-continue">Continue</Button>
            </DialogFooter>
          </>
        )}

        {step === "offer" && (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">Before you go</DialogTitle>
              <DialogDescription className="text-[15px] leading-6">
                {reason === "too_expensive"
                  ? "A smaller plan might fit better: Starter is $29 a month for 5,000 credits, and your unused credits carry over."
                  : reason === "project_done"
                    ? "Your unused credits stay in your account if you come back. You can also keep the plan and use what you've built up."
                    : `If you cancel, your ${planLabel(user?.plan)} plan stays active${endDate ? ` until ${endDate}` : " until the end of this billing period"}. After that you keep your lists, but reveals stop.`}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-2">
              {reason === "too_expensive" && (
                <Button variant="outline" onClick={() => { finish(); navigate("/choose-plan"); }}>See smaller plans</Button>
              )}
              <Button variant="outline" disabled={busy} onClick={() => submit("kept")} data-testid="cancel-keep">Keep my plan</Button>
              <Button variant="destructive" disabled={busy} onClick={() => submit("canceled")} data-testid="cancel-confirm">Cancel plan</Button>
            </DialogFooter>
          </>
        )}

        {step === "done" && (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">Your plan is cancelled</DialogTitle>
              <DialogDescription className="text-[15px] leading-6">
                It stays active{endDate ? ` until ${endDate}` : " until the end of this billing period"}, and you won't be charged again.
                Changed your mind? Use "Keep my plan" on the banner at any time before then.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button onClick={finish} data-testid="cancel-done">Done</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
